import React, { useState, useEffect } from 'react';
import { X, User, Clock, LogIn } from 'lucide-react';
import { useMessaging } from '../hooks/useMessaging';
import { LoginNotification } from '../types';

const LoginNotifications: React.FC = () => {
  const { loginNotifications, dismissLoginNotification } = useMessaging();

  if (loginNotifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2 max-w-sm">
      {loginNotifications.map((notification) => (
        <LoginNotificationItem
          key={notification.id}
          notification={notification}
          onDismiss={dismissLoginNotification}
        />
      ))}
    </div>
  );
};

interface LoginNotificationItemProps {
  notification: LoginNotification;
  onDismiss: (id: number) => void;
}

const LoginNotificationItem: React.FC<LoginNotificationItemProps> = ({ notification, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    
    // Auto dismiss after 3 seconds
    const dismissTimer = setTimeout(() => {
      handleDismiss();
    }, 3000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(dismissTimer);
    };
  }, []);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(notification.id);
    }, 300);
  };

  return (
    <div
      className={`transform transition-all duration-300 ease-in-out ${
        isVisible && !isLeaving 
          ? 'translate-x-0 opacity-100' 
          : 'translate-x-full opacity-0'
      }`}
    >
      <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <LogIn className="w-4 h-4 text-green-600" />
            </div>
          </div>
          
          <div className="ml-3 flex-1">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-green-800">
                Kullanıcı Girişi
              </h4>
              <button
                onClick={handleDismiss}
                className="text-green-400 hover:text-green-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="mt-1">
              <div className="flex items-center space-x-2">
                <img
                  className="w-6 h-6 rounded-full"
                  src={notification.user?.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                  alt={notification.user?.name}
                />
                <div>
                  <p className="text-sm text-green-700 font-medium">
                    {notification.user?.name}
                  </p>
                  <p className="text-xs text-green-600 capitalize">
                    {notification.user?.role}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center mt-2 text-xs text-green-600">
                <Clock className="w-3 h-3 mr-1" />
                <span>
                  {new Date(notification.login_time).toLocaleTimeString('tr-TR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default LoginNotifications;